import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'

import BackLink from '../BackLink'
import PageHeader from '../PageHeader'
import paymentMethodLabel from '../paymentMethodLabel'
// Per-method icon config (FA glyph + Tailwind color, plus per-method
// quirks like Venmo's rounded-sm) comes from the cross-surface registry
// shared with the Impact Hub checkout preview. PayPal/Venmo/Google Pay
// prefer the SVG brand marks in src/payments/logos — those are
// pixel-perfect; Apple Pay uses the FA brands faApple glyph (no SVG
// asset shipped). The consistent 1.25em wide × 1em tall sizing comes
// from the renderIcon wrapper so every method's icon column lines up
// regardless of source.
import { ICONS_TAILWIND as ICONS } from 'src/payments/paymentMethods'
import LOGOS from 'src/payments/logos'

// Cap the visible chooser at this many methods. Partners can enable
// up to ~12 methods today (the registry's icon table); past 6 the panel
// towers and the long tail dilutes the primary options. The 7th+
// land behind a one-way "More" reveal. Manager-decided number.
const VISIBLE_METHOD_LIMIT = 6

// Buttons use .btn-radio (the un-selected look) since these are
// navigation triggers — one click takes the user to the next step. We
// don't want any "checked" state since selection isn't a toggle. Layout
// is a single column for 2–3 methods (stacked, full width), 2 columns
// for 4+ (grid). Accessibility: the buttons are wrapped in a labeled
// group so screen readers announce the chooser context.
const PaymentMethodStep = ({ i18n, methods, onSelect, onBack }) => {
  const [ expanded, setExpanded ] = useState(false)

  // The donor's currency / frequency / tax-receipt choices can filter
  // every method out (bank-only in a non-USD currency; stock/DAF-only
  // with recurring or a Canadian-receipt/Gift-Aid option). Rather than
  // strand them on an empty chooser, explain why and offer Back to
  // adjust those choices. Early return keeps the normal render path
  // (below) free of empty-list special-casing.
  if (!methods.length) {
    return (
      <div className="space-y-4">
        <PageHeader heading={i18n.t('steps.payment_method.heading')} />
        <p
          role="alert"
          className="rounded border border-warning-300 bg-warning-50 text-warning-800 p-3 text-sm"
        >
          {i18n.t('steps.payment_method.none')}
        </p>
        <BackLink i18n={i18n} onClick={onBack} />
      </div>
    )
  }

  const hasOverflow = methods.length > VISIBLE_METHOD_LIMIT
  const visibleMethods = hasOverflow && !expanded
    ? methods.slice(0, VISIBLE_METHOD_LIMIT)
    : methods

  const layoutClass = methods.length >= 4
    ? 'grid grid-cols-2 gap-2'
    : 'flex flex-col gap-2'

  // Both branches render an icon that occupies 1.25em × 1em — fa-fw
  // does this for FontAwesome glyphs natively; the inline-flex
  // wrapper does the equivalent for SVG logos and uses object-contain
  // so logos with mismatched aspect ratios (Google Pay's wordmark,
  // PayPal's tall P, Venmo's square V) all fit without distortion
  // and align across the icon column. shrink-0 keeps either from
  // collapsing if the label text gets long.
  const renderIcon = method => {
    const config = ICONS[method] || {}
    const logo = LOGOS[method]
    if (logo) {
      return (
        <span className="shrink-0 inline-flex w-[1.25em] h-[1em] items-center justify-center">
          <img
            src={logo}
            alt=""
            aria-hidden="true"
            className={`max-h-full max-w-full object-contain ${config.className || ''}`}
          />
        </span>
      )
    }
    return config.icon && (
      <FontAwesomeIcon
        icon={config.icon}
        className={`shrink-0 fa-fw ${config.color}`}
      />
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader heading={i18n.t('steps.payment_method.heading')} />
      <div role="group" aria-label={i18n.t('payment_methods.payment_method')}>
        <div className={layoutClass}>
          {visibleMethods.map(method => (
            <button
              key={method}
              type="button"
              onClick={() => onSelect(method)}
              className="btn btn-radio flex items-center gap-2"
            >
              {renderIcon(method)}
              {paymentMethodLabel(method, i18n)}
            </button>
          ))}
        </div>
        {/* One-way reveal: the toggle removes itself when expanded so
            the post-expand layout is free of dead chrome. mt-2 matches
            the grid's gap-2 so the toggle sits a row's worth below the
            last method. Stays inside the role="group" so screen readers
            announce it as part of the picker, not a stray control. */}
        {hasOverflow && !expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="btn btn-link font-semibold mt-2 flex w-full items-center justify-center gap-2"
          >
            {i18n.t('steps.payment_method.show_more')}
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        )}
      </div>

      <BackLink i18n={i18n} onClick={onBack} />
    </div>
  )
}

export default PaymentMethodStep
